import { useState, useEffect, useCallback } from 'react';
import type { Note } from '../types';
import * as api from '../api';

interface UseNotesOptions {
  parentType: 'lead' | 'opportunity' | 'contact';
  parentId: string;
  limit?: number;
}

interface UseNotesResult {
  notes: Note[];
  pinnedNotes: Note[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refreshData: () => Promise<void>;
  createNote: (content: string, pinned?: boolean) => Promise<Note | null>;
  updateNote: (noteId: string, content: string) => Promise<Note | null>;
  deleteNote: (noteId: string) => Promise<boolean>;
  togglePin: (noteId: string) => Promise<Note | null>;
}

export function useNotes(options: UseNotesOptions): UseNotesResult {
  const { parentType, parentId, limit = 20 } = options;

  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const loadData = useCallback(async (loadMore = false) => {
    if (!parentId) return;

    try {
      if (!loadMore) {
        setLoading(true);
      }
      setError(null);

      const response = await api.fetchNotes({
        parent_type: parentType,
        parent_id: parentId,
        limit,
        cursor: loadMore ? cursor : undefined,
      });

      if (loadMore) {
        setNotes(prev => [...prev, ...response.data]);
      } else {
        setNotes(response.data);
      }

      setHasMore(response.pagination.has_more);
      setCursor(response.pagination.next_cursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [parentType, parentId, limit, cursor]);

  const loadMore = useCallback(async () => {
    if (hasMore && !loading) {
      await loadData(true);
    }
  }, [hasMore, loading, loadData]);

  const createNoteHandler = useCallback(async (
    content: string,
    pinned = false
  ): Promise<Note | null> => {
    try {
      const note = await api.createNote({
        parent_type: parentType,
        parent_id: parentId,
        content,
        pinned,
      });
      setNotes(prev => [note, ...prev]);
      return note;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create note');
      return null;
    }
  }, [parentType, parentId]);

  const updateNoteHandler = useCallback(async (
    noteId: string,
    content: string
  ): Promise<Note | null> => {
    try {
      const updated = await api.updateNote(noteId, content);
      setNotes(prev => prev.map(n => n.id === noteId ? updated : n));
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update note');
      return null;
    }
  }, []);

  const deleteNoteHandler = useCallback(async (noteId: string): Promise<boolean> => {
    try {
      await api.deleteNote(noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete note');
      return false;
    }
  }, []);

  const togglePinHandler = useCallback(async (noteId: string): Promise<Note | null> => {
    try {
      const updated = await api.toggleNotePin(noteId);
      setNotes(prev => prev.map(n => n.id === noteId ? updated : n));
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle pin');
      return null;
    }
  }, []);

  useEffect(() => {
    loadData(false);
  }, [parentType, parentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate pinned notes
  const pinnedNotes = notes.filter(n => n.pinned);
  const unpinnedNotes = notes.filter(n => !n.pinned);

  return {
    notes: unpinnedNotes,
    pinnedNotes,
    loading,
    error,
    hasMore,
    loadMore,
    refreshData: () => loadData(false),
    createNote: createNoteHandler,
    updateNote: updateNoteHandler,
    deleteNote: deleteNoteHandler,
    togglePin: togglePinHandler,
  };
}

export function formatNoteDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      if (diffMins < 1) return 'Just now';
      return `${diffMins}m ago`;
    }
    return `${diffHours}h ago`;
  }

  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}
